import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowDownUp, RefreshCw, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtIfNum = (n, digits) => {
  if (!Number.isFinite(n)) return "";
  return n.toFixed(digits);
};

export default function Calculator() {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState("CAD_TO_NGN");
  const [sendAmount, setSendAmount] = useState("100");
  const [receiveAmount, setReceiveAmount] = useState("");
  const [edited, setEdited] = useState("send"); // which field user last typed in

  const loadRates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/rates`);
      setRates(data);
    } catch (e) {
      console.error("Failed to load rates", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRates(); }, []);

  const { fromCcy, toCcy, rate } = useMemo(() => {
    if (!rates) return { fromCcy: "CAD", toCcy: "NGN", rate: 0 };
    if (direction === "CAD_TO_NGN")
      return { fromCcy: "CAD", toCcy: "NGN", rate: rates.cad_to_ngn_rate };
    return { fromCcy: "NGN", toCcy: "CAD", rate: rates.ngn_to_cad_rate };
  }, [rates, direction]);

  // Re-derive the OTHER input whenever the user types in one, or when the rate / direction changes
  useEffect(() => {
    if (!rate) return;
    if (edited === "send") {
      const n = parseFloat(sendAmount);
      if (Number.isFinite(n) && n > 0) {
        setReceiveAmount(fmtIfNum(n * rate, toCcy === "NGN" ? 2 : 4));
      } else if (sendAmount === "") {
        setReceiveAmount("");
      }
    } else {
      const n = parseFloat(receiveAmount);
      if (Number.isFinite(n) && n > 0) {
        setSendAmount(fmtIfNum(n / rate, fromCcy === "NGN" ? 2 : 4));
      } else if (receiveAmount === "") {
        setSendAmount("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendAmount, receiveAmount, rate, direction, edited]);

  const swap = () => {
    setDirection((d) => (d === "CAD_TO_NGN" ? "NGN_TO_CAD" : "CAD_TO_NGN"));
    setSendAmount("");
    setReceiveAmount("");
    setEdited("send");
  };

  return (
    <div
      data-testid="exchange-calculator"
      className="bg-white border border-slate-200 rounded-3xl shadow-[0_20px_60px_-20px_rgba(11,25,44,0.18)] p-6 sm:p-8 w-full max-w-md text-left"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF6B00]">Live calculator</div>
          <h3 className="font-display text-2xl font-bold text-[#0B192C] mt-1">Convert {fromCcy} → {toCcy}</h3>
        </div>
        <button
          onClick={loadRates}
          data-testid="refresh-rates-button"
          className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-[#0B192C] transition-colors"
          aria-label="Refresh rates"
        >
          <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="space-y-3">
        <label className="block">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">You send</span>
          <div className="mt-1.5 flex items-stretch rounded-2xl border border-slate-200 focus-within:border-[#FF6B00] focus-within:ring-2 focus-within:ring-[#FF6B00]/20 overflow-hidden">
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              value={sendAmount}
              onChange={(e) => { setEdited("send"); setSendAmount(e.target.value); }}
              placeholder="0.00"
              data-testid="send-amount-input"
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-xl font-semibold h-14 rounded-none"
            />
            <div className="flex items-center px-4 bg-slate-50 border-l border-slate-200 font-semibold text-[#0B192C]">
              {fromCcy}
            </div>
          </div>
        </label>

        <div className="flex justify-center -my-1">
          <button
            type="button"
            onClick={swap}
            data-testid="swap-direction-button"
            className="rounded-full bg-[#0B192C] hover:bg-[#162846] text-white p-2 shadow-md transition-colors"
            aria-label="Swap direction"
          >
            <ArrowDownUp size={18} />
          </button>
        </div>

        <label className="block">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Recipient gets</span>
          <div className="mt-1.5 flex items-stretch rounded-2xl border border-slate-200 focus-within:border-[#FF6B00] focus-within:ring-2 focus-within:ring-[#FF6B00]/20 overflow-hidden">
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              value={receiveAmount}
              onChange={(e) => { setEdited("receive"); setReceiveAmount(e.target.value); }}
              placeholder="0.00"
              data-testid="receive-amount"
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-xl font-semibold h-14 rounded-none"
            />
            <div className="flex items-center px-4 bg-slate-50 border-l border-slate-200 font-semibold text-[#0B192C]">
              {toCcy}
            </div>
          </div>
        </label>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="text-slate-500 font-medium">Rate</div>
          <div className="font-display font-bold text-[#0B192C] mt-0.5" data-testid="rate-display">
            1 {fromCcy} = {rate ? rate.toLocaleString("en-US", { maximumFractionDigits: toCcy === "NGN" ? 2 : 8 }) : "—"} {toCcy}
          </div>
        </div>
        <div className="bg-slate-50 rounded-xl p-3">
          <div className="text-slate-500 font-medium flex items-center gap-1">
            <TrendingUp size={12} /> Margin
          </div>
          <div className="font-display font-bold text-[#0B192C] mt-0.5" data-testid="margin-display">
            {rates ? "Margin included" : "—"}
          </div>
        </div>
      </div>

      <Link to="/register" data-testid="calculator-signup-button">
        <Button className="mt-6 w-full h-12 rounded-full bg-[#FF6B00] hover:bg-[#E65C00] text-white text-base font-semibold shadow-brand">
          Sign Up & Lock This Rate
        </Button>
      </Link>

      <p className="mt-3 text-[11px] text-slate-400 text-center" data-testid="rate-disclaimer">
        Rate source: {rates?.source ?? "—"}. Updates every 10 min. Final rate confirmed at transfer.
      </p>
    </div>
  );
}
